import React, { useState, useEffect, useCallback } from 'react';
import { Landmark, Play, RefreshCw, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { getFeatureFlags, listIraPeriods, getIraPeriod, runIraPeriod, certifyIraPeriod } from '../../config/api';
import PillTabs from '../../components/ui/PillTabs';

const money = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const date = (v) => (v ? new Date(v).toLocaleDateString() : '—');
const STATUS_PILL = {
  DRAFT: 'bg-gray-100 text-gray-700', CALCULATED: 'bg-blue-100 text-blue-700',
  CERTIFIED: 'bg-green-100 text-green-700', RESTATED: 'bg-purple-100 text-purple-700'
};
const PROGRAM_LABEL = { PART_B: 'Part B (ASP · quarterly)', PART_D: 'Part D (AMP · annual)' };

export default function IraRebates() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole ? hasRole(['ADMIN']) : false;

  const [flags, setFlags] = useState(null);
  const [program, setProgram] = useState('PART_B');
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ periodStart: '', periodEnd: '', benchmarkPeriod: '', currentCpiuPeriod: '' });

  const load = useCallback(async (prog) => {
    setLoading(true);
    try {
      const [f, p] = await Promise.all([flags ? Promise.resolve(flags) : getFeatureFlags(), listIraPeriods(prog)]);
      if (!flags) setFlags(f);
      setPeriods(p);
    } catch (e) { toast.error('Failed to load rebate periods'); }
    finally { setLoading(false); }
  }, [flags]);

  useEffect(() => { load(program); /* eslint-disable-next-line */ }, [program]);

  const programEnabled = (prog) => (prog === 'PART_B' ? flags?.iraPartB : flags?.iraPartD);
  const anyEnabled = flags && (flags.iraPartB || flags.iraPartD);

  const handleRun = async () => {
    if (!form.periodStart || !form.periodEnd) return toast.error('Set a period start and end');
    setBusy(true);
    try {
      const res = await runIraPeriod({ program, ...form });
      toast.success(`Ran ${PROGRAM_LABEL[program]} — ${res.resultCount} products, ${money(res.totalRebate)} rebate`);
      load(program);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to run period');
    } finally { setBusy(false); }
  };

  const openDetail = async (id) => {
    setDetail({ loading: true });
    try { setDetail(await getIraPeriod(id)); }
    catch (e) { toast.error('Failed to load period'); setDetail(null); }
  };

  const handleCertify = async (id) => {
    if (!window.confirm('Certify this rebate period? It freezes the results.')) return;
    try { await certifyIraPeriod(id); toast.success('Period certified'); openDetail(id); load(program); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to certify'); }
  };

  if (flags && !anyEnabled) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-lg rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <Landmark className="mx-auto mb-4 h-10 w-10 text-gray-300" />
          <h1 className="text-xl font-semibold text-gray-900">IRA Inflation Rebates</h1>
          <p className="mt-2 text-gray-600">This module isn't enabled. An admin can turn on Part B and/or Part D in Company Profile → Optional Modules.</p>
        </div>
      </div>
    );
  }

  // Detail view
  if (detail && !detail.loading) {
    const totalRebate = (detail.results || []).reduce((s, r) => s + Number(r.rebateAmount || 0), 0);
    return (
      <div className="p-6 space-y-5">
        <button onClick={() => setDetail(null)} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft size={16} /> Back to periods
        </button>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{PROGRAM_LABEL[detail.program] || detail.program}</h1>
              <p className="text-sm text-gray-500">{date(detail.periodStart)} – {date(detail.periodEnd)} · benchmark {detail.benchmarkPeriod || '—'}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_PILL[detail.status] || 'bg-gray-100'}`}>{detail.status}</span>
              {isAdmin && detail.status === 'CALCULATED' && (
                <button onClick={() => handleCertify(detail.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700">
                  <CheckCircle className="h-4 w-4" /> Certify
                </button>
              )}
            </div>
          </div>
          <div className="mt-4 inline-flex rounded-lg bg-primary-50 px-4 py-2">
            <span className="text-sm text-gray-600 mr-2">Total rebate liability:</span>
            <span className="font-bold text-primary-700">{money(totalRebate)}</span>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-right">Benchmark</th>
                <th className="px-4 py-3 text-right">Current</th>
                <th className="px-4 py-3 text-right">Infl.-adjusted</th>
                <th className="px-4 py-3 text-right">Excess/unit</th>
                <th className="px-4 py-3 text-right">Units</th>
                <th className="px-4 py-3 text-right">Rebate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(detail.results || []).map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-900">{r.product?.name || r.productId}</td>
                  <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">{money(r.benchmarkPrice)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">{money(r.currentPrice)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">{money(r.inflationAdjusted)}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${Number(r.excessPerUnit) > 0 ? 'text-red-600' : 'text-gray-500'}`}>{money(r.excessPerUnit)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">{Number(r.units).toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-gray-900 tabular-nums">{money(r.rebateAmount)}</td>
                </tr>
              ))}
              {(detail.results || []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No results — products need an AMP baseline, a CPI-U value for the period, and sales in the window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // List + run view
  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-primary-50 p-2.5"><Landmark className="h-6 w-6 text-primary-700" /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">IRA Inflation Rebates</h1>
          <p className="text-gray-600">Medicare inflation rebates — the price growth above CPI-U, times covered units.</p>
        </div>
      </div>

      <PillTabs
        active={program}
        onChange={setProgram}
        tabs={[
          { id: 'PART_B', label: 'Part B' },
          { id: 'PART_D', label: 'Part D' }
        ]}
      />

      {!programEnabled(program) ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {PROGRAM_LABEL[program]} isn't enabled. An admin can turn it on in Company Profile → Optional Modules.
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="mb-3 text-sm font-semibold text-gray-700">Run a {PROGRAM_LABEL[program]} period</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Period start</label><input type="date" value={form.periodStart} onChange={(e) => setForm((f) => ({ ...f, periodStart: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Period end</label><input type="date" value={form.periodEnd} onChange={(e) => setForm((f) => ({ ...f, periodEnd: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">CPI-U period</label><input placeholder="e.g. 2026Q1" value={form.currentCpiuPeriod} onChange={(e) => setForm((f) => ({ ...f, currentCpiuPeriod: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Benchmark label</label><input placeholder="e.g. 2021Q1" value={form.benchmarkPeriod} onChange={(e) => setForm((f) => ({ ...f, benchmarkPeriod: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={handleRun} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run period
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700">{PROGRAM_LABEL[program]} periods</h2>
          <button onClick={() => load(program)} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left">Period</th>
              <th className="px-4 py-3 text-left">Benchmark</th>
              <th className="px-4 py-3 text-right">Products</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            ) : periods.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">No periods yet. Run one above.</td></tr>
            ) : periods.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-900">{date(p.periodStart)} – {date(p.periodEnd)}</td>
                <td className="px-4 py-2.5 text-gray-600">{p.benchmarkPeriod || '—'}</td>
                <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">{p._count?.results ?? 0}</td>
                <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL[p.status] || 'bg-gray-100'}`}>{p.status}</span></td>
                <td className="px-4 py-2.5 text-gray-500">{date(p.createdAt)}</td>
                <td className="px-4 py-2.5 text-right"><button onClick={() => openDetail(p.id)} className="text-primary-600 hover:underline text-xs font-medium">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
